import { useUnmount } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { type Editor } from '@meitu/saas-editor-core';
import { type HighlightMaterial } from '@meitu/saas-editor-core/es/framework/services/text-highlight';
import { LayerType } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@/components/base-toolbar';
import { DividerLine } from '@/components/divider-line';
import { HeaderToolbar } from '@/components/header-toolbar';
import LabelAction from '@/components/label-action';
import { useBaseTool, useHeaderTool } from '@/hooks/use-common-tool';
import { EditorContext } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import { type ArtTextStruct, type ListStyleStruct } from '@/types';
import { TextEffectPanel } from './panels';
import { type ShapeTextMaterialRaw, type TexturePanelRenderFn, type WidgetCommonProps } from './types';
import { Tracker } from './utils';
import {
    ArtTextEntry,
    FontFamily,
    FontSize,
    FontStyle,
    HighlightEntry,
    LayerOpacity,
    ListStyle,
    ShapeTextEntry,
    TextAlignment,
    TextColorPicker,
    TextEffectEntry,
    TextSpacing,
} from './widgets';
import TextEffectController from './widgets/text-effect-controller';
import './style.less';
import './shared.less';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { FontItem } from '@meitu/xdesign-widgets';

type Props = {
    /**
     * 应用层主题
     */
    theme: string;
    /**
     * 是否是国际化环境
     */
    isIntl: boolean;
    /**
     * 编辑器实例
     */
    editor: Editor;
    /**
     * 当前编辑的图层 id
     */
    layerId: string;
    /**
     * 字体列表
     */
    fonts: FontItem[];
    /**
     * 变形文字列表
     */
    shapeTextList: ShapeTextMaterialRaw[];
    /**
     * 列表样式列表
     */
    listStyles: ListStyleStruct[];
    /**
     * 新版花字特效列表
     */
    artTexts?: ArtTextStruct[];
    /**
     * 划重点素材列表
     */
    highlights?: HighlightMaterial[];
    /**
     * 渲染纹理面板渲染问题（// FIXME: 历史问题，暂时先以当前形式实现）
     */
    renderTexturePanel: TexturePanelRenderFn;
    /**
     * 渲染品牌库文字弹窗触发器
     */
    renderBrandTextsPopupTrigger: () => JSX.Element | null;
    /**
     * 渲染文本内容重写模块
     */
    renderTextRewriter: () => JSX.Element | null;
    /**
     * 是否显示划重点入口
     */
    showHighlightEntry: boolean;
    /**
     * 是否显示 AI 花字入口
     */
    showArtTextEntry: boolean;
    /**
     * 是否显示特效预设列表
     */
    showEffectPresets?: boolean;
    /**
     * 免费使用文字特效素材
     */
    freeTextEffect?: boolean;
    /**
     * 应用层作图记录 id，主要用于埋点
     */
    recordId?: number;
    /**
     * 是否是 VIP 用户
     */
    isVIP?: boolean;
    /**
     * 打开 VIP 弹窗
     */
    onOpenVIPModal?: () => void;
    /**
     * 打开 AI 花字
     */
    onOpenArtText?: () => void;
};

const TextLayerPanel: React.FC<Props> = ({
    theme,
    isIntl,
    editor,
    layerId,
    fonts,
    shapeTextList,
    listStyles,
    artTexts,
    highlights = [],
    renderTexturePanel,
    renderBrandTextsPopupTrigger,
    renderTextRewriter,
    showArtTextEntry,
    showHighlightEntry,
    showEffectPresets = true,
    recordId,
    freeTextEffect = false,
    isVIP = false,
    onOpenVIPModal,
    onOpenArtText,
}) => {
    const { $t } = useLocaleStore();
    const headerToolCallback = useHeaderTool(editor, 1, recordId);
    const baseTrackCallback = useBaseTool(editor, 1, recordId);

    const [subPanel, setSubPanel] = useState<string | null>(null);
    const [isGroupLayer, setIsGroupLayer] = useState(false);
    const [subAttrType, setSubAttrType] = useState<string | null>(null);

    const hasFuncEntry =
        (!isGroupLayer && (showHighlightEntry || showArtTextEntry)) || (showArtTextEntry && onOpenArtText);

    const onOpenSubPanel = (subPanel: string, type?: string) => {
        setSubPanel(subPanel);
        if (type) setSubAttrType(type);
    };

    const closeSubPanel = () => {
        setSubPanel(null);
        setSubAttrType(null);
    };

    const handleAICallback = () => {
        Tracker.openArtTextPlugin({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const handleOpenArtText = () => {
        handleAICallback();
        if (onOpenArtText) onOpenArtText();
    };

    useEffect(() => {
        const layer = editor.services.frame.getActivePickLayer();

        const __isGroupLayer = layer?.layerType === LayerType.GROUP_LAYER;

        setIsGroupLayer(__isGroupLayer);
    }, [layerId]);

    // HACK: 文字编辑层的时候有在 mousedown 事件阻止冒泡
    // 但是阻止冒泡之后，点击文字编辑框就会影响弹出层自动收起
    // 后尝试在进入编辑态时调用 disableMouseSelect，退出编辑时调用 enableMouseSelect 发现不生效（disableMouseSelect 后仍可以拉动选框）
    // 郑鹏反馈这个问题不太好改，暂时先在业务方进行 HACK
    // todo 待确认
    // useEffect(() => {
    //     const dispose = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').onMouseDown(() => {
    //         const body = document.body;

    //         if (body) {
    //             const clickEvent = new MouseEvent('mousedown', {
    //                 bubbles: true,
    //                 cancelable: true,
    //                 view: window,
    //             });

    //             body.dispatchEvent(clickEvent);
    //         }
    //     });
    //     return () => dispose();
    // }, []);

    useUnmount(() => {
        const editingLayer = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').getEditingLayer();
        if (editingLayer && editingLayer.id === layerId)
            editor.getInstance<TextEditorPlugin>('TextEditorPlugin').exit();
    });

    const commonProps: WidgetCommonProps = {
        layerId,
        recordId,
        theme,
        isIntl,
        isVIP,
        onOpenVIPModal,
    };

    return (
        <EditorContext.Provider value={editor}>
            <div className="text-layer-panel">
                <HeaderToolbar
                    id={layerId}
                    editor={editor}
                    style={{ padding: 16 }}
                    BrandTextsPopup={renderBrandTextsPopupTrigger}
                    {...headerToolCallback}
                    onAI={handleAICallback}
                />

                {/*文本内容重写模块 */}
                {renderTextRewriter()}

                <div className="text-layer-panel__content">
                    <div className="text-layer-panel__block">
                        <FontFamily fonts={fonts} {...commonProps} />
                        <LabelAction title={$t((v) => v.textFontSizeAndTextColor.size)} align>
                            <div className="text-layer-panel__ctrl">
                                <FontSize {...commonProps} />
                                <TextColorPicker renderTexturePanel={renderTexturePanel} {...commonProps} />
                            </div>
                        </LabelAction>
                        <FontStyle {...commonProps} />
                        <TextAlignment {...commonProps} />
                        <LabelAction title={$t((v) => v.textAction.spacing)} align>
                            <div className="text-layer-panel__ctrl">
                                <TextSpacing {...commonProps} />
                                {!isGroupLayer ? <ListStyle listStyles={listStyles} {...commonProps} /> : null}
                            </div>
                        </LabelAction>
                    </div>

                    <DividerLine />

                    <div
                        className="text-layer-panel__block"
                        id="text-layer-panel__block"
                        style={{ flexDirection: 'row' }}
                    >
                        {!isGroupLayer ? (
                            <>
                                {/* 划重点入口 */}
                                {showHighlightEntry ? (
                                    <HighlightEntry highlights={highlights} {...commonProps} />
                                ) : null}

                                {/* 变形文字入口 */}
                                <ShapeTextEntry shapeTextList={shapeTextList} {...commonProps} />
                            </>
                        ) : null}

                        {showArtTextEntry && onOpenArtText ? <ArtTextEntry onClick={handleOpenArtText} /> : null}
                    </div>

                    {hasFuncEntry && <DividerLine />}

                    {/* 特效文字入口 */}
                    <div className="text-layer-panel__block">
                        <TextEffectEntry
                            free={freeTextEffect}
                            hidePresets={!showEffectPresets}
                            onClick={() => onOpenSubPanel('textEffect')}
                            artTexts={isGroupLayer ? undefined : artTexts} // ATTENTION: 不支持批量应用花字特效
                            {...commonProps}
                        />
                        <TextEffectController {...commonProps} onOpenSubPanel={onOpenSubPanel} />
                        <LayerOpacity {...commonProps} />
                    </div>
                    <DividerLine />

                    {/* 通用工具栏 */}
                    <BaseToolbar
                        id={layerId}
                        editor={editor}
                        style={{ padding: '0 16px 16px' }}
                        {...baseTrackCallback}
                    />
                </div>
            </div>
            {subPanel === 'textEffect' ? (
                <TextEffectPanel
                    type={subAttrType}
                    free={freeTextEffect}
                    hidePresets={!showEffectPresets}
                    onExit={closeSubPanel}
                    {...commonProps}
                />
            ) : null}
        </EditorContext.Provider>
    );
};

export default TextLayerPanel;
